import { useState, useContext, useEffect, useRef } from 'react';
import HeaderBackground from '../components/headerbackground.js';
import GlobalContext from "../context/GlobalContext.js";
import getAPI from '../functions/API/getAPI.js';
import { toast } from '../lib/toast.js';
import moment from 'moment';
import postAPI from '../functions/API/postAPI.js';
import { Mail, ClipboardCheck, ChevronRight, Trash2, MailOpen, Send, MessageSquare, ArrowLeft } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

export default function Messages() {
    const { theme, themeCSS, settings } = useContext(GlobalContext);
    const primaryColor = settings?.primary_hex_colour || '#813468';
    const navigate = useNavigate();
    const location = useLocation();

    // view: 'list' | 'detail' | 'chat'
    const [view, setView] = useState('list');
    const [loading, setLoading] = useState(true);
    const [messages, setMessages] = useState([]);
    const [selectedMessage, setSelectedMessage] = useState(null);
    const [selectedTaskMessage, setSelectedTaskMessage] = useState(null);
    const [taskChat, setTaskChat] = useState([]);
    const [chatLoading, setChatLoading] = useState(false);
    const [newMessage, setNewMessage] = useState('');
    const [sending, setSending] = useState(false);
    const [taskCompleted, setTaskCompleted] = useState(false);
    const pollingRef = useRef(null);
    const chatEndRef = useRef(null);

    async function fetchAll() {
        setLoading(true);
        let allData = [];

        try {
            const msgRes = await getAPI('messages');
            if (msgRes.response?.ok && msgRes.data) {
                const regular = [...msgRes.data].reverse().map(m => ({
                    ...m,
                    _type: 'message',
                    _sortDate: new Date(m.date + ' ' + (m.time || '00:00')),
                }));
                allData = [...allData, ...regular];
            }
        } catch {}

        try {
            const taskRes = await getAPI('taskmessages/inbox');
            if (taskRes.response?.ok && taskRes.data?.task_messages) {
                const tasks = taskRes.data.task_messages.map(m => ({
                    ...m,
                    _type: 'task',
                    _sortDate: new Date(m.created_at),
                    _instanceStatus: m.status,          // preserve task instance status before overwrite
                    status: m.unread_count > 0 ? 0 : 1, // repurpose for unread tracking
                }));
                allData = [...allData, ...tasks];
            }
        } catch {}

        allData.sort((a, b) => b._sortDate - a._sortDate);
        setMessages(allData);
        setLoading(false);
    }

    useEffect(() => {
        fetchAll();
        return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
    }, []);

    // Reset to list view whenever the tab is tapped (location.key changes on every navigate call)
    const initialRender = useRef(true);
    useEffect(() => {
        if (initialRender.current) { initialRender.current = false; return; }
        if (pollingRef.current) clearInterval(pollingRef.current);
        setView('list');
        setSelectedMessage(null);
        setSelectedTaskMessage(null);
        setTaskChat([]);
        setTaskCompleted(false);
        fetchAll();
    }, [location.key]);

    useEffect(() => {
        if (view === 'chat' && chatEndRef.current) {
            const container = chatEndRef.current.closest('.overflow-y-auto');
            if (container) {
                container.scrollTop = container.scrollHeight;
            }
        }
    }, [taskChat]);

    function openMessage(msg) {
        if (msg._type === 'message') {
            setSelectedMessage(msg);
            if (!msg.status) postAPI('messages/updatestatus', { id: msg.id, status: 1 });
            setView('detail');
        } else {
            openTaskChat(msg);
        }
    }

    const CLOSED_STATUSES = ['COMPLETED', 'CANCELLED'];

    async function openTaskChat(taskMsg) {
        // Determine completion immediately from inbox data (before any API call)
        const alreadyClosed = CLOSED_STATUSES.includes(taskMsg._instanceStatus);
        setTaskCompleted(alreadyClosed);

        // Optimistically mark as read in local state
        setMessages(prev => prev.map(m =>
            m._type === 'task' && m.task_instance_id === taskMsg.task_instance_id
                ? { ...m, status: 1, unread_count: 0 }
                : m
        ));
        setSelectedTaskMessage(taskMsg);
        setView('chat');
        setChatLoading(true);
        await loadTaskChat(taskMsg.task_instance_id);
        if (!alreadyClosed) {
            if (pollingRef.current) clearInterval(pollingRef.current);
            pollingRef.current = setInterval(() => loadTaskChat(taskMsg.task_instance_id), 5000);
        }
    }

    async function loadTaskChat(taskInstanceId) {
        const res = await postAPI('taskmessages/get', { task_instance_id: taskInstanceId });
        if (res.response?.ok) {
            if (res.data?.messages) setTaskChat(res.data.messages);
            // Also check task_instance status from chat response as authoritative source
            if (res.data?.task_instance?.status) {
                setTaskCompleted(CLOSED_STATUSES.includes(res.data.task_instance.status));
            }
        }
        setChatLoading(false);
    }

    async function sendTaskMessage() {
        if (!newMessage.trim() || sending) return;
        setSending(true);
        const res = await postAPI('taskmessages/send', {
            task_instance_id: selectedTaskMessage.task_instance_id,
            body: newMessage.trim(),
        });
        if (res.response?.ok) {
            setNewMessage('');
            await loadTaskChat(selectedTaskMessage.task_instance_id);
        } else {
            toast.error('Failed to send message.');
        }
        setSending(false);
    }

    async function deleteMessage() {
        const res = await postAPI('messages/delete', { id: selectedMessage.id });
        if (res.response?.ok) {
            toast.success('Message deleted');
            goBack();
        }
    }

    async function markUnread() {
        await postAPI('messages/updatestatus', { id: selectedMessage.id, status: 0 });
        goBack();
    }

    function goBack() {
        if (pollingRef.current) clearInterval(pollingRef.current);
        setView('list');
        setSelectedMessage(null);
        setSelectedTaskMessage(null);
        setTaskChat([]);
        setTaskCompleted(false);
        fetchAll();
    }

    const headerTitle = view === 'chat'
        ? (selectedTaskMessage?.task_flow_name || 'Message')
        : view === 'detail'
        ? (selectedMessage?.subject || 'Message')
        : 'Messages';

    // Icon background colours matching mobile (primary @ ~14% opacity vs gray)
    function iconBg(isUnread) {
        if (theme === 'highContrast') return isUnread ? '#000' : '#374151';
        if (isUnread) return `${primaryColor}22`;
        return theme === 'dark' ? '#334155' : '#F3F4F6';
    }
    function iconColor(isUnread) {
        if (theme === 'highContrast') return '#FBBF24';
        return isUnread ? primaryColor : (theme === 'dark' ? '#9CA3AF' : '#9CA3AF');
    }

    // ── Skeleton ────────────────────────────────────────────────────────
    function SkeletonList() {
        return (
            <div className={`${themeCSS.cardBg} rounded-2xl shadow-sm overflow-hidden`}>
                {[...Array(4)].map((_, i) => (
                    <div key={i} className={`flex items-center gap-3 px-4 py-4 ${i < 3 ? `border-b ${themeCSS.cardBorder}` : ''}`}>
                        <div className={`w-10 h-10 rounded-xl ${themeCSS.skeleton} animate-pulse flex-shrink-0`} />
                        <div className="flex-1 space-y-2">
                            <div className={`h-4 rounded-md ${themeCSS.skeleton} animate-pulse w-3/4`} />
                            <div className={`h-3 rounded-md ${themeCSS.skeleton} animate-pulse w-1/2`} />
                            <div className={`h-3 rounded-md ${themeCSS.skeleton} animate-pulse w-1/4`} />
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    // ── Message list ────────────────────────────────────────────────────
    function ListView() {
        return (
            <div className="flex-1 overflow-y-auto" style={{ backgroundColor: themeCSS.pageBg }}>
                <div className="max-w-2xl mx-auto px-4 py-4 pb-28">
                    {loading ? (
                        <SkeletonList />
                    ) : messages.length === 0 ? (
                        <div className={`${themeCSS.cardBg} rounded-2xl shadow-sm p-10 text-center`}>
                            <MessageSquare size={40} className="mx-auto mb-3" style={{ color: `${primaryColor}66` }} />
                            <p className={`${themeCSS.cardSubtext} text-sm`}>No messages yet</p>
                        </div>
                    ) : (
                        <div className={`${themeCSS.cardBg} rounded-2xl shadow-sm overflow-hidden`}>
                            {messages.map((msg, i) => {
                                const isTask = msg._type === 'task';
                                const isUnread = !msg.status;
                                const Icon = isTask ? ClipboardCheck : Mail;
                                const title = isTask ? (msg.task_flow_name || 'Task message') : msg.subject;
                                const preview = isTask ? msg.body : msg.message;
                                const date = isTask
                                    ? moment(msg.created_at).format('D MMM YYYY, HH:mm')
                                    : `${moment(msg.date).format('D MMM YYYY')} @ ${msg.time}`;
                                const unreadLabel = isTask && msg.unread_count > 1 ? msg.unread_count : 'New';

                                return (
                                    <button
                                        key={`${msg._type}-${msg.id || msg.task_instance_id}`}
                                        onClick={() => openMessage(msg)}
                                        className={`w-full flex items-center gap-3 px-4 py-4 text-left transition-colors ${themeCSS.cardHover} ${i < messages.length - 1 ? `border-b ${themeCSS.cardBorder}` : ''}`}
                                    >
                                        {/* Icon */}
                                        <div
                                            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                                            style={{ backgroundColor: iconBg(isUnread) }}
                                        >
                                            <Icon size={20} strokeWidth={1.5} style={{ color: iconColor(isUnread) }} />
                                        </div>

                                        {/* Content */}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center justify-between gap-2 mb-0.5">
                                                <p className={`text-sm truncate ${isUnread ? `font-bold ${themeCSS.cardTitle}` : themeCSS.cardText}`}>
                                                    {title}
                                                </p>
                                                {isUnread && (
                                                    <span
                                                        className="flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-lg flex-shrink-0"
                                                        style={{ color: primaryColor, backgroundColor: `${primaryColor}22` }}
                                                    >
                                                        <span
                                                            className="w-1.5 h-1.5 rounded-full inline-block"
                                                            style={{ backgroundColor: primaryColor }}
                                                        />
                                                        {unreadLabel}
                                                    </span>
                                                )}
                                            </div>
                                            <p className={`text-sm truncate ${themeCSS.cardSubtext}`}>
                                                {preview}
                                            </p>
                                            <div className="flex items-center gap-2 mt-1">
                                                {isTask && (
                                                    <span
                                                        className="text-[11px] font-semibold px-1.5 py-0.5 rounded"
                                                        style={{ color: primaryColor, backgroundColor: `${primaryColor}22` }}
                                                    >
                                                        Request
                                                    </span>
                                                )}
                                                <span className={`text-xs ${themeCSS.cardMuted}`}>{date}</span>
                                            </div>
                                        </div>

                                        <ChevronRight size={18} className={`flex-shrink-0 ${themeCSS.cardMuted}`} strokeWidth={1.5} />
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // ── Regular message detail ───────────────────────────────────────────
    function DetailView() {
        return (
            <div className="flex-1 overflow-y-auto" style={{ backgroundColor: themeCSS.pageBg }}>
                <div className="max-w-2xl mx-auto px-4 py-4 pb-28">
                    {/* Desktop back button */}
                    <button
                        onClick={goBack}
                        className={`hidden lg:flex items-center gap-2 mb-4 text-sm ${themeCSS.cardSubtext} hover:${themeCSS.cardText} transition-colors`}
                    >
                        <ArrowLeft size={16} />
                        Back to messages
                    </button>

                    <div className={`${themeCSS.cardBg} rounded-2xl shadow-sm p-6`}>
                        <h2 className={`text-xl font-bold leading-snug ${themeCSS.cardTitle}`}>
                            {selectedMessage?.subject}
                        </h2>
                        <p className={`text-sm mt-1 ${themeCSS.cardSubtext}`}>
                            {moment(selectedMessage?.date).format('dddd, Do MMMM YYYY')}
                            {selectedMessage?.time && ` at ${selectedMessage.time}`}
                        </p>
                        <div className={`border-t ${themeCSS.cardBorder} my-4`} />
                        <p className={`text-base leading-relaxed ${themeCSS.cardText}`}>
                            {selectedMessage?.message}
                        </p>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-3 mt-4">
                        <button
                            onClick={deleteMessage}
                            className="flex items-center gap-2 px-4 py-2.5 bg-red-50 text-red-600 hover:bg-red-100 rounded-xl text-sm font-medium transition-colors"
                        >
                            <Trash2 size={16} />
                            Delete
                        </button>
                        {selectedMessage?.status === 1 && (
                            <button
                                onClick={markUnread}
                                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${themeCSS.cardHover} ${themeCSS.cardText}`}
                                style={{ backgroundColor: theme === 'dark' ? '#334155' : '#F3F4F6' }}
                            >
                                <MailOpen size={16} />
                                Mark as unread
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    // ── Task conversation thread ─────────────────────────────────────────
    function ChatView() {
        return (
            <div className="flex flex-col flex-1 min-h-0" style={{ backgroundColor: themeCSS.pageBg }}>
                {/* Desktop back button + View full request card */}
                <div className="max-w-2xl mx-auto w-full px-4 pt-4 flex-shrink-0">
                    <button
                        onClick={goBack}
                        className={`hidden lg:flex items-center gap-2 mb-4 text-sm ${themeCSS.cardSubtext} hover:${themeCSS.cardText} transition-colors`}
                    >
                        <ArrowLeft size={16} />
                        Back to messages
                    </button>

                    {/* View full request card */}
                    <button
                        onClick={() => navigate('/requests', { state: { openInstanceId: selectedTaskMessage?.task_instance_id } })}
                        className={`w-full ${themeCSS.cardBg} rounded-2xl px-4 py-3.5 flex items-center gap-3 shadow-sm ${themeCSS.cardHover} transition-colors mb-2`}
                    >
                        <div
                            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: `${primaryColor}22` }}
                        >
                            <ClipboardCheck size={20} strokeWidth={1.5} style={{ color: primaryColor }} />
                        </div>
                        <div className="flex-1 text-left min-w-0">
                            <p className={`text-sm font-semibold ${themeCSS.cardTitle}`}>View full request</p>
                            <p className={`text-sm truncate ${themeCSS.cardSubtext}`}>
                                {selectedTaskMessage?.task_flow_name}
                            </p>
                        </div>
                        <ChevronRight size={18} className={themeCSS.cardMuted} strokeWidth={1.5} />
                    </button>
                </div>

                {/* Chat messages */}
                <div className="flex-1 overflow-y-auto">
                    <div className="max-w-2xl mx-auto px-4 py-2">
                        {chatLoading && taskChat.length === 0 ? (
                            <div className="flex justify-center py-8">
                                <div className={`w-8 h-8 rounded-full border-2 border-t-transparent animate-spin`}
                                    style={{ borderColor: `${primaryColor}44`, borderTopColor: primaryColor }} />
                            </div>
                        ) : taskChat.length === 0 ? (
                            <p className={`text-center text-sm py-6 ${themeCSS.cardSubtext}`}>No messages yet. Start the conversation below.</p>
                        ) : (
                            taskChat.map((msg) => {
                                const isTenant = msg.role === 'tenant' || msg.sender_role === 'tenant';
                                return (
                                    <div key={msg.id} className={`flex mb-3 ${isTenant ? 'justify-end' : 'justify-start'}`}>
                                        <div
                                            className="max-w-[75%] rounded-2xl px-4 py-3"
                                            style={{
                                                backgroundColor: isTenant
                                                    ? `${primaryColor}22`
                                                    : (theme === 'dark' ? '#334155' : '#FFFFFF'),
                                                border: isTenant ? 'none' : `1px solid ${theme === 'dark' ? '#475569' : '#E5E7EB'}`,
                                            }}
                                        >
                                            <div className="flex items-center gap-2 mb-1">
                                                <span
                                                    className="text-xs font-semibold"
                                                    style={{ color: isTenant ? primaryColor : (theme === 'dark' ? '#D1D5DB' : '#374151') }}
                                                >
                                                    {isTenant ? 'You' : (msg.sender_name || 'Admin')}
                                                </span>
                                                <span className={`text-xs ${themeCSS.cardMuted}`}>
                                                    {moment(msg.created_at).format('D MMM YYYY, HH:mm')}
                                                </span>
                                            </div>
                                            {msg.subject && (
                                                <p className={`text-sm font-medium mb-1 ${themeCSS.cardText}`}>{msg.subject}</p>
                                            )}
                                            <p className={`text-sm leading-relaxed ${themeCSS.cardText}`}>{msg.body}</p>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                        <div ref={chatEndRef} />
                    </div>
                </div>

                {/* Disclaimer */}
                {!taskCompleted && (
                    <p className={`text-xs text-center px-6 py-2 ${themeCSS.cardMuted} flex-shrink-0`}>
                        Messages are reviewed in the order that they are received. We will come back to you as soon as we can.
                    </p>
                )}

                {/* Input bar — pb-28 on mobile gives clearance above the floating tab bar */}
                <div
                    className={`flex-shrink-0 px-3 pt-2.5 pb-28 lg:pb-4 ${themeCSS.cardBg}`}
                    style={{ borderTop: `1px solid ${theme === 'dark' ? '#334155' : '#E5E5EA'}` }}
                >
                    <div className="max-w-2xl mx-auto">
                        {taskCompleted ? (
                            <div className={`flex items-center justify-center gap-2 rounded-full px-4 py-3 ${theme === 'dark' ? 'bg-slate-700' : 'bg-gray-100'}`}>
                                <p className={`text-sm ${themeCSS.cardMuted}`}>This request is closed — messaging unavailable</p>
                            </div>
                        ) : (
                            <div className={`flex items-center rounded-full pl-4 pr-1.5 py-1.5 ${theme === 'dark' ? 'bg-slate-700' : 'bg-gray-100'}`}>
                                <input
                                    type="text"
                                    value={newMessage}
                                    onChange={e => setNewMessage(e.target.value)}
                                    onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendTaskMessage()}
                                    placeholder="Type a message..."
                                    maxLength={2000}
                                    className={`flex-1 text-sm bg-transparent ${themeCSS.cardText} min-w-0 border-0 outline-none ring-0 shadow-none focus:outline-none focus:ring-0 focus:border-0 focus:shadow-none`}
                                    style={{ caretColor: primaryColor }}
                                />
                                <button
                                    onClick={sendTaskMessage}
                                    disabled={!newMessage.trim() || sending}
                                    className="w-8 h-8 rounded-full flex items-center justify-center transition-opacity disabled:opacity-40 flex-shrink-0"
                                    style={{ backgroundColor: primaryColor }}
                                >
                                    <Send size={15} className="text-white" strokeWidth={2} />
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-screen" style={{ backgroundColor: themeCSS.pageBg }}>
            <div className="flex-shrink-0">
                <HeaderBackground
                    pageTitle={headerTitle}
                    onBack={view !== 'list' ? goBack : undefined}
                />
            </div>
            {view === 'list' && ListView()}
            {view === 'detail' && DetailView()}
            {view === 'chat' && ChatView()}
        </div>
    );
}
